/**
 * Геометрия CAD-режимов Визуализатора: лассо hit-test и поворот.
 */

export function pointInRing(
  lng: number,
  lat: number,
  ring: [number, number][],
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function ringBBox(ring: [number, number][]): {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
} {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  }
  return { minLng, minLat, maxLng, maxLat };
}

function pointInBBox(
  lng: number,
  lat: number,
  box: ReturnType<typeof ringBBox>,
): boolean {
  return lng >= box.minLng && lng <= box.maxLng && lat >= box.minLat && lat <= box.maxLat;
}

/** Представительная точка знака для лассо (якорь / центроид кольца). */
export function signRepresentativePoint(
  geometry: GeoJSON.Geometry,
): [number, number] | null {
  if (geometry.type === "Point") {
    return geometry.coordinates as [number, number];
  }
  if (geometry.type === "LineString") {
    const coords = geometry.coordinates;
    if (!coords.length) return null;
    const mid = coords[Math.floor(coords.length / 2)];
    return [mid[0], mid[1]];
  }
  if (geometry.type === "Polygon") {
    const ring = geometry.coordinates[0] ?? [];
    if (!ring.length) return null;
    let sx = 0;
    let sy = 0;
    const n = ring.length > 1 ? ring.length - 1 : ring.length;
    for (let i = 0; i < n; i++) {
      sx += ring[i][0];
      sy += ring[i][1];
    }
    return [sx / n, sy / n];
  }
  return null;
}

export function signsInsideLasso(
  features: GeoJSON.Feature[],
  lassoRing: [number, number][],
): number[] {
  if (lassoRing.length < 3) return [];
  const closed =
    lassoRing[0][0] === lassoRing[lassoRing.length - 1][0] &&
    lassoRing[0][1] === lassoRing[lassoRing.length - 1][1]
      ? lassoRing
      : ([...lassoRing, lassoRing[0]] as [number, number][]);
  const box = ringBBox(closed);
  const ids: number[] = [];
  for (const feature of features) {
    if (!feature.geometry || feature.geometry.type === "GeometryCollection") continue;
    const pt = signRepresentativePoint(feature.geometry);
    if (!pt) continue;
    if (!pointInBBox(pt[0], pt[1], box)) continue;
    if (!pointInRing(pt[0], pt[1], closed)) continue;
    const id = Number(feature.properties?.id ?? feature.id);
    if (Number.isFinite(id)) ids.push(id);
  }
  return ids;
}

export function geometryCentroid(
  geometry: GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon,
): [number, number] {
  const pt = signRepresentativePoint(geometry);
  if (pt) return pt;
  return [0, 0];
}

function rotatePoint(
  lng: number,
  lat: number,
  cx: number,
  cy: number,
  angleRad: number,
): [number, number] {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const dx = lng - cx;
  const dy = lat - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
}

export function rotateGeometry(
  geometry: GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon,
  angleDeg: number,
  center?: [number, number],
): GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon {
  const [cx, cy] = center ?? geometryCentroid(geometry);
  const rad = (angleDeg * Math.PI) / 180;
  if (geometry.type === "Point") {
    const [lng, lat] = geometry.coordinates;
    return { type: "Point", coordinates: rotatePoint(lng, lat, cx, cy, rad) };
  }
  if (geometry.type === "LineString") {
    return {
      type: "LineString",
      coordinates: geometry.coordinates.map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, rad)),
    };
  }
  return {
    type: "Polygon",
    coordinates: geometry.coordinates.map((ring) =>
      ring.map(([lng, lat]) => rotatePoint(lng, lat, cx, cy, rad)),
    ),
  };
}

export function angleDegBetween(
  cx: number,
  cy: number,
  lng: number,
  lat: number,
): number {
  return (Math.atan2(lat - cy, lng - cx) * 180) / Math.PI;
}

/** Рабочие вершины без дубля замыкания полигона. */
export function editableRingCoords(
  geometry: GeoJSON.LineString | GeoJSON.Polygon,
): [number, number][] {
  if (geometry.type === "LineString") {
    return geometry.coordinates.map(([lng, lat]) => [lng, lat] as [number, number]);
  }
  const ring = geometry.coordinates[0] ?? [];
  if (
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
  ) {
    return ring.slice(0, -1).map(([lng, lat]) => [lng, lat] as [number, number]);
  }
  return ring.map(([lng, lat]) => [lng, lat] as [number, number]);
}

export function rebuildShapeGeometry(
  type: "LineString" | "Polygon",
  coords: [number, number][],
): GeoJSON.LineString | GeoJSON.Polygon {
  if (type === "LineString") {
    return { type: "LineString", coordinates: coords.map(([lng, lat]) => [lng, lat]) };
  }
  if (coords.length === 0) {
    return { type: "Polygon", coordinates: [[]] };
  }
  const closed = [...coords, coords[0]] as [number, number][];
  return { type: "Polygon", coordinates: [closed.map(([lng, lat]) => [lng, lat])] };
}

export type VertexHandleFeatureProps = {
  handleKind: "vertex" | "midpoint";
  /** Индекс вершины или индекс ребра (вставка после). */
  vertexIndex: number;
  selected?: boolean;
};

export function buildVertexHandleFeatures(
  geometry: GeoJSON.LineString | GeoJSON.Polygon,
  selectedVertexIndex: number | null = null,
): GeoJSON.Feature[] {
  const coords = editableRingCoords(geometry);
  const features: GeoJSON.Feature[] = [];
  for (let i = 0; i < coords.length; i++) {
    features.push({
      type: "Feature",
      properties: {
        handleKind: "vertex",
        vertexIndex: i,
        selected: selectedVertexIndex === i,
      } satisfies VertexHandleFeatureProps,
      geometry: { type: "Point", coordinates: coords[i] },
    });
  }
  const edgeCount = geometry.type === "Polygon" ? coords.length : Math.max(0, coords.length - 1);
  for (let i = 0; i < edgeCount; i++) {
    const a = coords[i];
    const b = coords[(i + 1) % coords.length];
    features.push({
      type: "Feature",
      properties: {
        handleKind: "midpoint",
        vertexIndex: i,
        selected: false,
      } satisfies VertexHandleFeatureProps,
      geometry: {
        type: "Point",
        coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      },
    });
  }
  return features;
}

export function moveVertexAt(
  geometry: GeoJSON.LineString | GeoJSON.Polygon,
  index: number,
  lng: number,
  lat: number,
): GeoJSON.LineString | GeoJSON.Polygon | null {
  const coords = editableRingCoords(geometry);
  if (index < 0 || index >= coords.length) return null;
  const next = coords.map((c, i) => (i === index ? ([lng, lat] as [number, number]) : c));
  return rebuildShapeGeometry(geometry.type, next);
}

export function insertVertexAfter(
  geometry: GeoJSON.LineString | GeoJSON.Polygon,
  edgeIndex: number,
  lng: number,
  lat: number,
): GeoJSON.LineString | GeoJSON.Polygon | null {
  const coords = editableRingCoords(geometry);
  const edgeCount = geometry.type === "Polygon" ? coords.length : Math.max(0, coords.length - 1);
  if (edgeIndex < 0 || edgeIndex >= edgeCount) return null;
  const next = [
    ...coords.slice(0, edgeIndex + 1),
    [lng, lat] as [number, number],
    ...coords.slice(edgeIndex + 1),
  ];
  return rebuildShapeGeometry(geometry.type, next);
}

export type DeleteVertexResult =
  | { ok: true; geometry: GeoJSON.LineString | GeoJSON.Polygon }
  | { ok: false; reason: "min" | "index" };

export function deleteVertexAt(
  geometry: GeoJSON.LineString | GeoJSON.Polygon,
  index: number,
): DeleteVertexResult {
  const coords = editableRingCoords(geometry);
  if (index < 0 || index >= coords.length) return { ok: false, reason: "index" };
  const min = geometry.type === "LineString" ? 2 : 3;
  if (coords.length <= min) return { ok: false, reason: "min" };
  const next = coords.filter((_, i) => i !== index);
  return { ok: true, geometry: rebuildShapeGeometry(geometry.type, next) };
}
